<script module>
  import { gql } from '$lib/graphql.svelte.js'
  import {
    OfferFragmentSegments,
    OfferFragment
  } from '$lib/OfferSelection.svelte'

  export const i18n = {
    en: {
      YOUTH: 'Youth',
      SENIOR: 'Senior',
      ADULT: 'Adult',
      CHILD: 'Child',
      '2ndclass': '2nd class',
      '1stclass': '1st class'
    },
    sv: {
      YOUTH: 'Ungdom',
      SENIOR: 'Senior',
      ADULT: 'Vuxen',
      CHILD: 'Barn',
      '2ndclass': '2:a klass',
      '1stclass': '1:a klass',
      Back: 'Tillbaka',
      'Go to payment': 'Gå till betalning',
      'Place order': 'Slutför beställning',
      'YYYY-MM-DD or DD/MM/YYYY': 'YYYY-MM-DD eller DD/MM/YYYY',
      Continue: 'Fortsätt',
      'Confirm payment': 'Betala',
      'Your purchase is securely handled by %s. We prioritize your security and privacy, see %s.':
        'Ditt köp hanteras säkert av %s. Vi prioriterar din säkerhet och integritet, se %s.',
      'our privacy policy': 'vår integritetspolicy',
      'Terms must be accepted': 'Villkoren måste accepteras',
      'Passenger birthdate is invalid':
        'Passagerarens födelsedatum är ogiltigt',
      'Email is required': 'E-post krävs',
      'Phone number is required': 'Telefonnummer krävs',
      'Missing passenger birthdate': 'Passagerarens födelsedatum saknas',
      'Could not save passengers': 'Kunde inte spara passagerare',
      'Could not complete booking': 'Kunde inte slutföra bokning',
      'Something’s wrong': 'Något gick fel',
      'Please check that everything is correct and try again.':
        'Kontrollera att alla uppgifter stämmer och försök igen.',
      'It could be a temporary issue. Please try again.':
        'Det kan vara ett tillfälligt problem. Försök igen.'
    },
    da: {
      YOUTH: 'Ungdom',
      SENIOR: 'Senior',
      ADULT: 'Voksen',
      CHILD: 'Barn',
      '2ndclass': '2. klasse',
      '1stclass': '1. klasse',
      Back: 'Tilbage',
      'Go to payment': 'Gå til betaling',
      'Place order': 'Gennemfør bestilling',
      'YYYY-MM-DD or DD/MM/YYYY': 'YYYY-MM-DD eller DD/MM/YYYY',
      Continue: 'Fortsæt',
      'Confirm payment': 'Betal',
      'Your purchase is securely handled by %s. We prioritize your security and privacy, see %s.':
        'Dit køb håndteres sikkert af %s. Vi prioriterer din sikkerhed og privatliv, se %s.',
      'our privacy policy': 'vores privatlivspolitik',
      'Terms must be accepted': 'Vilkårene skal accepteres',
      'Passenger birthdate is invalid': 'Passagerens fødselsdato er ugyldig',
      'Email is required': 'E-mail er påkrævet',
      'Phone number is required': 'Telefonnummer er påkrævet',
      'Missing passenger birthdate': 'Passagerens fødselsdato mangler',
      'Could not complete booking': 'Kunne ikke fuldføre booking',
      'Something’s wrong': 'Noget gik galt',
      'Please check that everything is correct and try again.':
        'Kontroller, at alt er korrekt, og prøv igen.',
      'It could be a temporary issue. Please try again.':
        'Det kan være et midlertidigt problem. Prøv igen.'
    }
  }

  export const BookingFragment = gql`
    fragment booking on Booking {
      __typename
      id
      expiresAt
      totalPrice {
        amount
        currency
      }
      ticketingFee {
        amount
        currency
      }
      passengers {
        __typename
        id
        age
        type
        ... on FullPassenger {
          firstName
          lastName
          tel
          email
          birthDate
          title
          countryResidence
          isContactPerson
          nationality
          passportNumber
          placeOfBirthCity
          placeOfBirthCountry
        }
      }
      requirements {
        countryResidence
        passportNumber
        placeOfBirthCity
        placeOfBirthCountry
        dateOfBirth
        nationality
        title
        email
        tel
      }
      termsAndConditions
      selections {
        __typename
        ... on JourneyOfferSelection {
          offers {
            ...${OfferFragment}
          }
          journey {
            itinerary {
              __typename
              ... on SegmentCollection {
                offers {
                  ...${OfferFragment}
                }
                segments {
                  ...${OfferFragmentSegments}
                }
              }
            }
          }
        }
        ... on PassBundleSelection {
          product
          class
          price {
            amount
            currency
          }
          passengers {
            count
            type
          }
          travelDayCount
          validityPeriod
        }
      }
    }
  `

  export const BookingFragmentWithGiftCards = gql`
    fragment bookingWithGiftCards on Booking {
      ...${BookingFragment}
      selections {
        __typename
        ... on AppliedGiftCard {
          code
          value {
            amount
            currency
          }
        }
        ... on GiftCardSelection {
          price {
            amount
            currency
          }
        }
      }
    }
  `
</script>

<script>
  import { invalidateAll, goto, preloadCode } from '$app/navigation'
  import { enhance, applyAction } from '$app/forms'
  import { page, navigating } from '$app/state'
  import { browser } from '$app/environment'
  import { dev } from '$app/environment'
  import { parseJSON } from 'date-fns'

  import Header, { i18n as headerI18n } from '$lib/Header.svelte'
  import { setPayment } from './payment/payment.svelte.js'
  import { register } from '$lib/utils/text.svelte.js'
  import { track } from '$lib/plausible.svelte.js'
  import State from '$lib/utils/state.svelte.js'
  import { update } from '$lib/Intercom.svelte'
  import Breakdown from './Breakdown.svelte'
  import { setBooking } from './booking.js'
  import Symbol from '$lib/Symbol.svelte'
  import Button from '$lib/Button.svelte'
  import Footer from '$lib/Footer.svelte'
  import Config from '$lib/Config.svelte'
  import Modal from '$lib/Modal.svelte'
  import Meta from '$lib/Meta.svelte'
  import Html from '$lib/Html.svelte'

  let text = register(i18n, headerI18n)

  let { agent, checkout } = page.data

  /** @type {Schema.Booking} */
  let booking = $state({ ...page.data.booking })

  setBooking(booking)

  /** @type {{ children: Svelte.Snippet }} */
  let { children } = $props()

  const payment = setPayment()

  let queued = null
  let submitting = $state(false)
  let intermediate = $state(false)

  /** @type {import('@sveltejs/kit').SubmitFunction<{ booking: Schema.Booking }, App.ActionFailure>} */
  const onsubmit = async ({ formData, cancel }) => {
    if (formData.has('offer')) {
      const offerId = String(formData.get('offer'))
      intermediate = true
      booking.selections = booking.selections?.map((selection) => {
        if (selection.__typename !== 'JourneyOfferSelection') return selection

        const segmentCollection = /** @type {Schema.SegmentCollection?} */ (
          selection.journey.itinerary.find(
            (item) =>
              item.__typename === 'SegmentCollection' &&
              item.offers?.some((offer) => offer.id === offerId)
          )
        )

        if (!segmentCollection) return selection

        const offer = segmentCollection.offers?.find(
          (offer) => offer.id === offerId
        )

        if (!offer) return selection

        const index = selection.offers.findIndex((offer) =>
          segmentCollection.offers?.some((_offer) => _offer.id === offer.id)
        )

        if (index === -1) return selection

        return {
          ...selection,
          offers: [
            ...selection.offers.slice(0, index),
            offer,
            ...selection.offers.slice(index + 1)
          ]
        }
      })
    } else if (formData.has('add-ancillary')) {
      intermediate = true

      const value = String(formData.get('add-ancillary'))
      const [, ancillaryId, passengerId] = value.split('.')

      booking.selections = booking.selections?.map((selection) => {
        if (selection.__typename !== 'JourneyOfferSelection') return selection

        const offers = selection.offers.map((offer) => {
          const parts = offer.parts.map((part) => {
            if (part.__typename !== 'ReservationPart') return part

            let ancillaries = part.ancillaries?.map((ancillary) => {
              if (ancillary.id !== ancillaryId) return ancillary
              const selections = [
                ...ancillary.selections,
                { passengerId, id: 'PLACEHOLDER' }
              ]
              return { ...ancillary, selections }
            })

            return { ...part, ancillaries }
          })

          return { ...offer, parts }
        })

        return { ...selection, offers }
      })
    } else if (formData.has('remove-ancillary')) {
      intermediate = true

      const value = String(formData.get('remove-ancillary'))
      const [, ancillarySelectionId] = value.split('.')

      booking.selections = booking.selections?.map((selection) => {
        if (selection.__typename !== 'JourneyOfferSelection') return selection

        const offers = selection.offers.map((offer) => {
          const parts = offer.parts.map((part) => {
            if (part.__typename !== 'ReservationPart') return part

            const ancillaries = part.ancillaries?.map((ancillary) => {
              const selections = ancillary.selections?.filter((selection) => {
                return selection.id !== ancillarySelectionId
              })
              return { ...ancillary, selections }
            })

            return { ...part, ancillaries }
          })

          return { ...offer, parts }
        })

        return { ...selection, offers }
      })
    } else if (submitting) {
      cancel()
      return
    }

    submitting = true

    if (formData.has('next') && step === 'payment') {
      payment.confirm()
      cancel()
      return
    }

    const current = (queued = {})
    return async ({ result }) => {
      if (current === queued) {
        if (result.type === 'success' && result.data?.booking) {
          Object.assign(booking, result.data.booking)
        } else {
          await applyAction(result)
        }
        intermediate = false
        submitting = false
      }
    }
  }

  const resolve = (doc) => {
    const prefix = State.prefixForLanguage(
      doc.lang.substring(0, 2),
      agent.settings?.data.default_language
    )
    let href = `${prefix}/booking/${page.params.booking}/${step}`
    if (page.params.payment) href += `/${page.params.payment}`
    return href
  }

  let expiresAt = $derived(
    booking?.expiresAt ? parseJSON(`${booking.expiresAt}:00`) : null
  )
  let hasJourneyOfferSelection = $derived(
    booking?.selections?.some(
      (selection) => selection.__typename === 'JourneyOfferSelection'
    )
  )
  let step = $derived(page.route.id?.match(/\/(\w+?)(?:\/\[\w+\])?$/)?.[1])
  let bypassPayment = $derived(agent.paymentMethod !== 'STRIPE')
  let steps = $derived(
    booking
      ? [
          {
            name: hasJourneyOfferSelection ? 'planning' : 'selection'
          },
          hasJourneyOfferSelection
            ? { name: 'tickets', selected: step === 'tickets' }
            : null,
          {
            name: 'passengers',
            selected: step === 'passengers'
          },
          {
            name: bypassPayment ? 'order' : 'payment',
            selected: step === 'payment'
          }
        ].filter(Boolean)
      : null
  )
  let languages = $derived(
    checkout?.alternate_languages.length
      ? [{ href: resolve(checkout), lang: checkout.lang }].concat(
          checkout.alternate_languages.map((doc) => ({
            href: resolve(doc),
            lang: doc.lang
          }))
        )
      : []
  )

  /** @type {Schema.FullPassenger|undefined} */
  // @ts-ignore
  let contactPerson = $derived(
    booking?.passengers?.find(
      (passenger) =>
        passenger.__typename === 'FullPassenger' && passenger.isContactPerson
    )
  )

  $effect(() => {
    if (contactPerson?.email && !bypassPayment) {
      update({
        name:
          [contactPerson.firstName, contactPerson.lastName]
            .filter(Boolean)
            .join(' ') || undefined,
        email: contactPerson.email
      })
    }
  })

  let timeout
  const watchExpiration = () => {
    clearTimeout(timeout)
    if (expiresAt) {
      timeout = setTimeout(invalidateAll, +expiresAt - Date.now())
    }
  }

  $effect(watchExpiration)

  $effect(() => {
    if (step === 'tickets') {
      preloadCode(`${State.prefix}/booking/${page.params.booking}/passengers`)
    }
  })

  $effect(() => {
    if (browser) {
      track('Checkout', { props: { step } })
    }
  })

  let back = $derived(
    step === 'payment'
      ? `${State.prefix}/booking/${page.params.booking}/passengers`
      : step === 'passengers' && hasJourneyOfferSelection
        ? `${State.prefix}/booking/${page.params.booking}/tickets`
        : page.data.referrer
  )
</script>

<svelte:document on:visibilitychange={watchExpiration} />

<Meta title={text(steps?.find((step) => step.selected)?.name || '')} />

{#if page.form && page.status >= 400}
  {@const validationErrors = page.form.extra?.errors?.filter(
    (err) => err.extensions?.code === 'VALIDATION_ERROR'
  )}
  <Modal
    onclose={() => {
      if (page.form.extra?.fatal) {
        if (page.data.referrer) {
          goto(page.data.referrer)
        } else if (agent.settings?.data.website?.url) {
          window.location.assign(agent.settings.data.website.url)
        } else {
          goto('/')
        }
      } else {
        invalidateAll()
      }
    }}>
    <Html>
      <h2>{text(/** @type {string} */ (page.form.message))}</h2>
      <p>
        {#if page.form.extra?.fatal}
          {text`It could be a temporary issue. Please try again.`}
        {:else}
          {text`Please check that everything is correct and try again.`}
        {/if}
      </p>
      {#if validationErrors?.length}
        <ul>
          {#each validationErrors as error}
            <li>{error.message}</li>
          {/each}
        </ul>
      {/if}
      {#if dev}
        <pre>{JSON.stringify(page.form.extra, null, 2)}</pre>
      {/if}
    </Html>
  </Modal>
{/if}

<Config
  theme="smoke"
  primary={agent.settings?.data.primary_color}
  secondary={agent.settings?.data.secondary_color}>
  <div class="booking">
    <Header {languages} {steps} navigation={false}>
      {#snippet primary()}
        {#if back}
          <a class="back" href={back}>
            <span class="arrow"><Symbol name="arrow" /></span>
            {text`Back`}
          </a>
        {/if}
      {/snippet}
    </Header>

    <form
      method="POST"
      class="layout"
      autocomplete="off"
      autocapitalize="off"
      action="?/{step}"
      use:enhance={onsubmit}>
      <fieldset class="main">
        {@render children()}
      </fieldset>

      {#if !page.error}
        <fieldset class="summary theme-reset">
          <Breakdown {intermediate} {booking}>
            {#snippet action()}
              <Button
                success
                size="lg"
                name="next"
                type="submit"
                formaction="?/next"
                visuallyDisabled={submitting && !intermediate}>
                {#if (submitting && !intermediate) || navigating.type === 'form'}
                  <span class="spinner">
                    <Symbol name="spinner" size="fill" prefix />
                  </span>
                {/if}
                {step === 'passengers'
                  ? bypassPayment
                    ? text`Place order`
                    : text`Go to payment`
                  : step === 'payment'
                    ? text`Confirm payment`
                    : text`Continue`}
              </Button>
            {/snippet}
            {#snippet message()}
              <div class="message">
                {#if page.form && page.status >= 400}
                  <Html class="u-textMd u-textMuted">
                    <p>
                      <strong>
                        {text(/** @type {string} */ (page.form.message))}.
                      </strong>
                      {#if page.form.extra?.fatal}
                        {text`Please contact us for assistance.`}
                      {:else}
                        {text`Please check that everything is correct and try again.`}
                      {/if}
                    </p>
                  </Html>
                {/if}
                {#if agent.uid !== 'all-aboard' && !bypassPayment}
                  <div class="fineprint">
                    <Html class="u-textMuted" center>
                      <p>
                        {@html text`Your purchase is securely handled by ${`<a
                    href="https://allaboard.eu/?utm_source=${agent.uid}&utm_content=booking"
                    target="_blank">All&nbsp;Aboard</a>`}. We prioritize your security and privacy, see ${`<a
                    href="https://allaboard.eu/privacy-policy?utm_source=${agent.uid}&utm_content=booking"
                    target="_blank">${text`our privacy policy`}</a>`}.`}
                      </p>
                    </Html>
                  </div>
                {/if}
              </div>
            {/snippet}
          </Breakdown>
        </fieldset>
      {/if}
      <input type="hidden" name="metadata.via_portal" value={page.url.origin} />
    </form>

    <div class="footer theme-reset">
      <Footer internal />
    </div>
  </div>
</Config>

<style>
  .booking {
    display: flex;
    flex-direction: column;
    min-height: 100%;
    background: var(--background);
  }

  .back {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: -0.2rem;
    font-size: 1rem;
  }

  .arrow {
    display: inline-block;
    transition: transform 190ms var(--ease-out);
    transform: translateY(-0.03rem) rotate(0.5turn);
  }

  .back:hover .arrow {
    transform: translateY(-0.03rem) rotate(0.5turn) translateX(0.15rem);
  }

  .layout {
    flex: 1 0 auto;
    display: flex;
    align-items: start;
    flex-wrap: wrap;
    gap: 2rem;
    padding-top: clamp(3rem, 10vw, 5rem);
  }

  @media (width >= 1000px) {
    .layout {
      flex-wrap: nowrap;
      gap: clamp(2rem, 3.5vw, 4rem);
      margin: 0 auto;
      width: min(
        calc(100vw - var(--document-margin) * 2),
        var(--container-large-width)
      );
    }

    @supports (padding-left: env(safe-area-inset-left)) {
      .layout {
        padding-left: env(safe-area-inset-left);
        padding-right: env(safe-area-inset-right);
      }
    }
  }

  .main {
    min-width: 0;
    margin-bottom: clamp(2rem, 10vh, 5rem);
  }

  @media (width < 1000px) {
    .main {
      margin: 0 auto;
      width: min(
        calc(100vw - var(--document-margin) * 2),
        var(--container-large-width)
      );
    }

    @supports (padding-left: env(safe-area-inset-left)) {
      .main {
        padding-left: env(safe-area-inset-left);
        padding-right: env(safe-area-inset-right);
      }
    }
  }

  @media (width > 1000px) {
    .main {
      flex: 1 1 auto;
    }
  }

  .summary {
    flex: 0 0 100%;
    padding: clamp(1rem, 1.5vw, 1.5rem) clamp(1.25rem, 2vw, 1.75rem) 2.5rem;
    margin-top: auto;
    position: sticky;
    top: 1.5rem;
    background: var(--background);
    border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
  }

  @supports (padding-left: env(safe-area-inset-left)) {
    .summary {
      padding-left: calc(var(--document-margin) + env(safe-area-inset-left));
      padding-right: calc(var(--document-margin) + env(safe-area-inset-right));
    }
  }

  @media (width >= 1000px) {
    .summary {
      flex: 0 0 clamp(18rem, 28vw, 25rem);
      padding: clamp(1rem, 1.5vw, 1.5rem) clamp(1.25rem, 2vw, 1.75rem);
      margin: 0;
      box-shadow: var(--box-shadow-sm);
      border-radius: var(--border-radius-lg);
    }
  }

  .spinner {
    flex: 0 0 auto;
    height: 1em;
    margin-right: 0.25em;
  }

  .message {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .message:empty {
    display: none;
  }

  .fineprint {
    --current-font-size: 0.875rem;

    display: flex;
    justify-content: center;
  }

  @media (width < 1000px) {
    .footer {
      --current-background: var(--background);
    }
  }

  @media (width >= 1000px) {
    .footer {
      border-top: 1px solid rgba(var(--rgb-gray), 0.25);
    }
  }
</style>
